import { pickT } from "@repo/shared";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { usePortfolioSnapshot } from "@/lib/hooks/use-portfolio-snapshot";
import { HistoricalReturnWidget } from "../historical-return-widget";
import { T } from "./historical-return.i18n";
import type { PortfolioInfo } from "./types";

type HistoricalReturnProps = {
  portfolioId: string | null;
  config: { style?: string; title?: string; description?: string };
  language: string;
  portfolioInfo: PortfolioInfo;
  /**
   * Phase 2 PR-A (S4) — engine reported `kind: "snapshot"` for this component on this
   * portfolio. When set AND prerequisites are met but the historical_return snapshot row is
   * missing, replace the "not available" fallback with a clear snapshot-missing
   * placeholder pointing at the regenerate banner.
   */
  hasValidationError?: boolean;
};

export function HistoricalReturn({
  portfolioId,
  config,
  language,
  portfolioInfo,
  hasValidationError = false,
}: HistoricalReturnProps) {
  const t = pickT(T, language);

  const historicalReturn = usePortfolioSnapshot("historicalReturn", portfolioId);
  const portfolio = usePortfolioSnapshot("portfolio", portfolioId);

  // Loading
  if (historicalReturn.isLoading || portfolio.isLoading) return null;

  // No data
  if (!historicalReturn.data) {
    // QL-311 — empty-state contract order: custom first. A custom portfolio's
    // inputs live in the builder; the risk/amount prompts below would send the
    // client to fields that don't gate it. A custom portfolio WITH products
    // whose snapshot is missing falls through to the system branches — unlike
    // asset-allocation there is no "calculating" placeholder here, because
    // `notAvailable` names a real cause (products without linked instruments).
    if (portfolioInfo.isCustomPortfolio) {
      if (!portfolio.data) {
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.noProductsHistoricalReturn}
          </div>
        );
      }
    } else {
      if (!portfolioInfo.hasRiskAssessment) {
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {t.completeRiskOrBuildCustom}
          </div>
        );
      }
      if (!portfolio.data) {
        // QL-311 — a missing amount blocks snapshot generation too; without
        // this branch it fell through to the no-portfolio-for-score message,
        // blaming collection configuration for a missing user input.
        if (!portfolioInfo.hasInvestmentAmount) {
          return (
            <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
              {t.enterInvestmentAmount}
            </div>
          );
        }
        return (
          <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
            {portfolioInfo.riskScore != null
              ? t.noPortfolioForScore(String(portfolioInfo.riskScore))
              : t.noPortfolioGeneric}
          </div>
        );
      }
    }
    // PR-A (S4) — D20 N/A means prereq branches above wouldn't trigger validation.
    // Reaching this fallback with hasValidationError=true means prereqs are met but the
    // historical_return snapshot row is missing (system-bug case).
    if (hasValidationError) {
      return (
        <div className="text-sm text-destructive border border-destructive rounded-lg p-4">
          {t.snapshotMissingMessage}
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.notAvailable}
      </div>
    );
  }

  // Parse data (API returns jsonb, may already be an object)
  const snapshot =
    typeof historicalReturn.data === "string"
      ? (() => {
          try {
            return JSON.parse(historicalReturn.data as string);
          } catch {
            return null;
          }
        })()
      : historicalReturn.data;

  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoading}</div>;
  }

  // Error in snapshot
  if (snapshot.error) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        <div className="font-medium mb-1">{t.unavailable}</div>
        <div>{String(snapshot.error)}</div>
      </div>
    );
  }

  return (
    <VisualizationContainer config={config}>
      <HistoricalReturnWidget snapshot={snapshot} language={language} />
    </VisualizationContainer>
  );
}
